import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RichReportEditor from './RichReportEditor';
import doctorService from '../services/doctorService';
import type { Doctor } from '../services/doctorService';

interface SaveTemplateBindingDialogProps {
  open: boolean;
  onClose: () => void;
  doctor: Doctor | null;
  cptCode?: string | null;
  modality?: string | null;
  bodyRegion?: string | null;
  customPromptOverride?: string | null;
  existingBindingId?: number | null;
  onSaved?: (customPromptOverride: string) => void;
}

const SaveTemplateBindingDialog: React.FC<SaveTemplateBindingDialogProps> = ({
  open,
  onClose,
  doctor,
  cptCode,
  modality,
  bodyRegion,
  customPromptOverride,
  existingBindingId,
  onSaved,
}) => {
  const [promptText, setPromptText] = useState('');
  const [saveForAllExams, setSaveForAllExams] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bindingToOverride, setBindingToOverride] = useState<number | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (open) {
      setPromptText(customPromptOverride || '');
      setSaveForAllExams(!cptCode);
      setError(null);
      setBindingToOverride(null);
      setIsPreview(false);
    }
  }, [open, customPromptOverride, cptCode]);

  const saveBinding = async (bindingId?: number) => {
    if (!doctor || !promptText.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await doctorService.saveAfterGeneration(doctor.id, {
        cpt_code: saveForAllExams ? undefined : (cptCode || undefined),
        modality: saveForAllExams ? undefined : (modality || undefined),
        body_region: saveForAllExams ? undefined : (bodyRegion || undefined),
        custom_prompt_override: promptText.trim(),
        existing_binding_id: bindingId,
      });
      onSaved?.(promptText.trim());
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(error?.response?.data?.detail || error?.message || 'Failed to save binding');
    } finally {
      setSaving(false);
      setBindingToOverride(null);
    }
  };

  const handleSave = async () => {
    if (!doctor || !promptText.trim()) return;
    if (existingBindingId) {
      setBindingToOverride(existingBindingId);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const targetCpt = saveForAllExams ? null : (cptCode || null);
      const targetModality = saveForAllExams ? null : (modality || null);
      const targetBodyRegion = saveForAllExams ? null : (bodyRegion || null);
      const { items } = await doctorService.listBindings(doctor.id);
      const matchingBinding = items.find((binding) => {
        if (!binding.is_active) return false;
        const bCpt = binding.cpt_code ?? null;
        const bModality = binding.modality ?? null;
        const bBodyRegion = binding.body_region ?? null;
        return bCpt === targetCpt && bModality === targetModality && bBodyRegion === targetBodyRegion;
      });
      if (matchingBinding) {
        setBindingToOverride(matchingBinding.id);
        return;
      }
      await saveBinding();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(error?.response?.data?.detail || error?.message || 'Failed to check existing bindings');
    } finally {
      setSaving(false);
    }
  };

  const isUpdate = !!existingBindingId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SaveIcon color="primary" />
        {isUpdate ? 'Update Doctor Template Binding' : 'Save Doctor Template Binding'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Doctor info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Doctor:
          </Typography>
          <Chip size="small" label={doctor?.name || 'Unknown'} color="primary" />
          {doctor?.specialty && (
            <Chip size="small" label={doctor.specialty} variant="outlined" />
          )}
        </Box>

        {/* CPT info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            CPT:
          </Typography>
          {cptCode && !saveForAllExams ? (
            <Chip size="small" label={cptCode} color="success" />
          ) : (
            <Chip size="small" label="All exams (doctor-wide)" variant="outlined" />
          )}
        </Box>

        {/* Scope toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={saveForAllExams}
              onChange={(e) => setSaveForAllExams(e.target.checked)}
              disabled={!cptCode}
              size="small"
            />
          }
          label="Apply to all exam types (doctor-wide binding)"
          sx={{ mb: 2, display: 'block' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Custom Prompt Override</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              startIcon={<EditIcon />}
              variant={isPreview ? 'text' : 'contained'}
              onClick={() => setIsPreview(false)}
              disabled={saving}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              variant={isPreview ? 'contained' : 'text'}
              onClick={() => setIsPreview(true)}
              disabled={saving}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Preview
            </Button>
          </Box>
        </Box>
        <Box sx={{ height: 260 }}>
          <RichReportEditor
            value={promptText}
            onChange={setPromptText}
            editing={!isPreview && !saving}
            preserveFormatting
            placeholder="Enter the custom template prompt for this doctor."
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          This prompt will be used for report generation when this doctor is selected
          {cptCode && !saveForAllExams ? ` for CPT ${cptCode}` : ' for all exam types'}.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          disabled={saving || !promptText.trim() || !doctor}
        >
          {isUpdate ? 'Update Binding' : 'Save Binding'}
        </Button>
      </DialogActions>
      <Dialog
        open={bindingToOverride !== null}
        onClose={() => !saving && setBindingToOverride(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Replace existing template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A template binding already exists for {saveForAllExams ? 'all exam types' : `CPT ${cptCode}`}. Replacing it will permanently update the template used for {doctor?.name}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBindingToOverride(null)} disabled={saving}>Cancel</Button>
          <Button
            onClick={() => saveBinding(bindingToOverride ?? undefined)}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Replacing...' : 'Replace Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SaveTemplateBindingDialog;
